/**
 * 认证相关Hook
 * 简化认证状态管理和操作
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../store';
import { tokenManager } from '../utils/tokenManager';
import {
  useLoginMutation,
  useLogoutApiMutation,
  useGetCurrentUserQuery,
  logout as logoutAction,
  loginSuccess,
  setRememberMe,
  clearError,
  UserRole,
  quickLogin,
} from '../store/slices/authSlice';

// 临时类型定义
interface LoginRequest {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export const useAuth = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();

  // 从Redux状态获取认证信息
  const {
    user,
    token,
    isAuthenticated,
    isLoading,
    error,
    rememberMe,
    lastActivity,
  } = useAppSelector(state => state.auth);

  // API mutations和queries
  const [loginMutation, { isLoading: isLoggingIn }] = useLoginMutation();
  const [logoutMutation, { isLoading: isLoggingOut }] = useLogoutApiMutation();
  const { refetch: refetchCurrentUser } = useGetCurrentUserQuery(undefined, {
    skip: !isAuthenticated,
  });

  // 登录函数
  const login = useCallback(
    async (credentials: LoginRequest) => {
      try {
        const result = await loginMutation(credentials).unwrap();

        // 处理登录成功，先保存token到本地存储
        const tokenInfo = {
          accessToken: result.tokens.accessToken,
          refreshToken: result.tokens.refreshToken,
          user: result.user,
        };

        // 先更新 rememberMe 状态
        const shouldRemember = credentials.rememberMe || false;
        dispatch(setRememberMe(shouldRemember));

        // 保存到本地存储，传递 rememberMe 参数
        tokenManager.setTokenInfo(tokenInfo, shouldRemember);

        // 更新Redux状态
        dispatch(
          loginSuccess({
            user: result.user,
            token: result.tokens.accessToken,
            refreshToken: result.tokens.refreshToken,
          })
        );

        // 检查是否有重定向路径
        const redirectPath = localStorage.getItem('redirectAfterLogin');
        if (redirectPath) {
          localStorage.removeItem('redirectAfterLogin');
          // 安全验证：只允许相对路径或同源URL
          let safeRedirectPath = '/';
          try {
            // 检查是否是相对路径
            if (
              redirectPath.startsWith('/') &&
              !redirectPath.startsWith('//')
            ) {
              safeRedirectPath = redirectPath;
            } else {
              // 检查是否是同源URL
              const url = new URL(redirectPath, window.location.origin);
              if (url.origin === window.location.origin) {
                safeRedirectPath = url.pathname + url.search;
              }
            }
          } catch {
            // URL解析失败，使用默认路径
            safeRedirectPath = '/';
          }
          // 延迟重定向，确保状态已更新
          setTimeout(() => {
            window.location.href = safeRedirectPath;
          }, 100);
        } else {
          // 检查是否有待编辑的问卷
          const pendingEdit = localStorage.getItem('pendingQuestionnaireEdit');
          if (pendingEdit) {
            try {
              const editInfo = JSON.parse(pendingEdit);
              localStorage.removeItem('pendingQuestionnaireEdit');

              // 延迟导航到问卷编辑页面
              setTimeout(() => {
                window.location.href = `/questionnaire?editMode=${editInfo.editMode}&resubmitMode=${editInfo.resubmitMode}&questionnaireId=${editInfo.questionnaireId}`;
              }, 100);
            } catch (error) {
              console.error(
                'Failed to parse pending questionnaire edit info:',
                error
              );
              localStorage.removeItem('pendingQuestionnaireEdit');
            }
          }
        }

        console.log('Login succeeded - useAuth processing completed', {
          tokenExists: !!tokenManager.getToken(),
          userInfo: tokenManager.getUser()?.email,
        });

        return { success: true, data: result };
      } catch (error: any) {
        // 根据后端错误码显示本地化消息，避免英文环境展示中文后端文本
        let errorMessage = t('auth:login.failed');

        if (error?.data?.error) {
          const errorCode = error.data.error.code;
          const backendMessage: string | undefined = error.data.error.message;
          const hasChineseInMessage =
            typeof backendMessage === 'string' &&
            /[\u4e00-\u9fff]/.test(backendMessage);
          const shouldUseBackendMessage =
            !!backendMessage &&
            (i18n.language.startsWith('zh') || !hasChineseInMessage);

          const getMessage = (fallbackKey: string) =>
            shouldUseBackendMessage ? backendMessage! : t(fallbackKey);

          // 根据错误代码优先处理
          switch (errorCode) {
            case 'AUTH_ACCOUNT_NOT_ACTIVATED':
              errorMessage = getMessage('auth:login.accountNotActivated');
              break;
            case 'AUTH_ACCOUNT_DISABLED':
              errorMessage = getMessage('auth:login.accountDisabled');
              break;
            case 'AUTH_ACCOUNT_LOCKED':
              errorMessage = getMessage('auth:login.accountLocked');
              break;
            case 'AUTH_INVALID_CREDENTIALS':
              errorMessage = getMessage('auth:login.invalidCredentials');
              break;
            default:
              errorMessage = shouldUseBackendMessage
                ? backendMessage!
                : t('auth:login.failed');
          }
        } else if (error?.message) {
          errorMessage = t('common:message.networkError');
        }

        return {
          success: false,
          error: errorMessage,
        };
      }
    },
    [loginMutation, dispatch, i18n.language, t]
  );

  // 登出函数
  const logout = useCallback(async () => {
    try {
      await logoutMutation().unwrap();
      dispatch(logoutAction());
      return { success: true };
    } catch (error: any) {
      // 即使API调用失败，也要清除本地状态
      dispatch(logoutAction());
      return { success: true };
    }
  }, [logoutMutation, dispatch]);

  // 快速登录（用于开发测试）
  const quickLoginHandler = useCallback(
    async (role: UserRole) => {
      try {
        await dispatch(quickLogin(role)).unwrap();
        return { success: true };
      } catch (error: any) {
        return {
          success: false,
          error: error?.message || t('auth:login.failed'),
        };
      }
    },
    [dispatch, t]
  );

  // 更新记住我设置
  const updateRememberMe = useCallback(
    (remember: boolean) => {
      dispatch(setRememberMe(remember));
    },
    [dispatch]
  );

  // 清除错误
  const clearAuthError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  // 刷新用户信息
  const refreshUser = useCallback(async () => {
    try {
      await refetchCurrentUser();
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error?.data?.message || t('common:message.loadError'),
      };
    }
  }, [refetchCurrentUser, t]);

  // 检查权限
  const hasPermission = useCallback(
    (permission: string) => {
      // 如果用户未登录，拒绝所有权限
      if (!user || !isAuthenticated) {
        return false;
      }

      // 管理员拥有所有权限
      if (user.role === UserRole.ADMIN) {
        return true;
      }

      // 检查用户是否拥有特定权限
      // 基于角色的默认权限映射
      const rolePermissions: Record<string, string[]> = {
        [UserRole.ISSUER]: [
          'dashboard',
          'assessment',
          'issuance',
          'workbench',
          'project-management',
        ],
        [UserRole.EXCHANGE]: [
          'dashboard',
          'screening',
          'review',
          'management',
          'user-management',
          'channel-management',
        ],
        [UserRole.ADMIN]: ['*'], // 管理员拥有所有权限
      };

      const userPermissions = rolePermissions[user.role] || [];

      // 检查是否拥有通配符权限或具体权限
      return (
        userPermissions.includes('*') || userPermissions.includes(permission)
      );
    },
    [user, isAuthenticated]
  );

  // 检查角色
  const hasRole = useCallback(
    (role: UserRole) => {
      return user?.role === role;
    },
    [user?.role]
  );

  // 检查是否为发行人
  const isIssuer = useCallback(() => {
    return user?.role === UserRole.ISSUER;
  }, [user?.role]);

  // 检查是否为交易所
  const isExchange = useCallback(() => {
    return user?.role === UserRole.EXCHANGE;
  }, [user?.role]);

  // 检查是否为管理员
  const isAdmin = useCallback(() => {
    return user?.role === UserRole.ADMIN;
  }, [user?.role]);

  // 获取用户显示名称
  const getUserDisplayName = useCallback(() => {
    if (!user) return '';
    return user.name || user.email || t('common:header.user');
  }, [user, t]);

  // 检查会话是否活跃
  const isSessionActive = useCallback(() => {
    if (!lastActivity) return false;
    const now = Date.now();
    const maxInactivity = 30 * 60 * 1000; // 30分钟
    return now - lastActivity < maxInactivity;
  }, [lastActivity]);

  return {
    // 状态
    user,
    token,
    isAuthenticated,
    isLoading: isLoading || isLoggingIn || isLoggingOut,
    error,
    rememberMe,
    permissions: [],
    lastActivity,

    // 操作函数
    login,
    logout,
    quickLogin: quickLoginHandler,
    updateRememberMe,
    clearAuthError,
    refreshUser,

    // 权限检查
    hasPermission,
    hasRole,
    isIssuer,
    isExchange,
    isAdmin,

    // 工具函数
    getUserDisplayName,
    isSessionActive,

    // 加载状态
    isLoggingIn,
    isLoggingOut,
  };
};
